import React, { useState, useEffect, useRef } from 'react';
import { BrandContentEditor } from './admin/BrandContentEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FeatureEditor() {
  const [features, setFeatures] = useState([]);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [featureDetail, setFeatureDetail] = useState(null);
  const [customizations, setCustomizations] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedFields, setEditedFields] = useState({});
  const [message, setMessage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editorMode, setEditorMode] = useState('feature');
  const fileInputRef = useRef(null);

  // Fetch all features list
  useEffect(() => {
    fetchFeatures();
    fetchCustomizations();
  }, []);

  const fetchFeatures = async () => {
    try {
      const response = await fetch(`${API_URL}/api/lead-nurturing/features-drip`);
      const data = await response.json();
      if (data.success) {
        setFeatures(data.features);
      }
    } catch (error) {
      console.error('Error fetching features:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomizations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/feature-admin/features`);
      const data = await response.json();
      if (data.success) {
        const customMap = {};
        data.customizations.forEach(c => {
          customMap[c.feature_day] = c;
        });
        setCustomizations(customMap);
      }
    } catch (error) {
      console.error('Error fetching customizations:', error);
    }
  };

  const selectFeature = async (feature) => {
    setSelectedFeature(feature);
    setEditMode(false);
    setEditedFields({});
    
    try {
      const response = await fetch(`${API_URL}/api/lead-nurturing/feature-detail/${feature.day}?language=hi`);
      const data = await response.json();
      if (data.success) {
        setFeatureDetail(data.detail);
        // Pre-fill edit fields
        setEditedFields({
          hero_image: data.detail?.hero_image || '',
          problems_title_hi: data.detail?.problems?.title_hi || '',
          problems_title_en: data.detail?.problems?.title_en || '',
          problems_description_hi: data.detail?.problems?.description_hi || '',
          problems_description_en: data.detail?.problems?.description_en || '',
          solution_title_hi: data.detail?.solution?.title_hi || '',
          solution_title_en: data.detail?.solution?.title_en || '',
          solution_description_hi: data.detail?.solution?.description_hi || '',
          solution_description_en: data.detail?.solution?.description_en || ''
        });
      }
    } catch (error) {
      console.error('Error fetching feature detail:', error);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedFields(prev => ({ ...prev, [field]: value }));
  };

  const saveChanges = async () => {
    if (!selectedFeature) return;
    
    setSaving(true);
    setMessage(null);
    
    try {
      const response = await fetch(`${API_URL}/api/feature-admin/update-full`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          feature_day: selectedFeature.day,
          hero_image: editedFields.hero_image || null,
          problems_title_hi: editedFields.problems_title_hi || null,
          problems_title_en: editedFields.problems_title_en || null,
          problems_description_hi: editedFields.problems_description_hi || null,
          problems_description_en: editedFields.problems_description_en || null,
          solution_title_hi: editedFields.solution_title_hi || null,
          solution_title_en: editedFields.solution_title_en || null,
          solution_description_hi: editedFields.solution_description_hi || null,
          solution_description_en: editedFields.solution_description_en || null
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: 'Changes saved successfully!' });
        setEditMode(false);
        fetchCustomizations();
        // Refresh feature detail
        selectFeature(selectedFeature);
      } else {
        setMessage({ type: 'error', text: 'Failed to save changes' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving changes' });
    } finally {
      setSaving(false);
    }
  };

  const revertToDefault = async () => {
    if (!selectedFeature) return;
    
    if (!window.confirm('Are you sure you want to revert to default content?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/feature-admin/feature/${selectedFeature.day}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: 'Reverted to default content!' });
        fetchCustomizations();
        selectFeature(selectedFeature);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error reverting changes' });
    }
  };

  // Image upload handler
  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setMessage({ type: 'error', text: 'Invalid file type. Only JPG, PNG, GIF, WebP allowed.' });
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'File too large. Maximum 5MB allowed.' });
      return;
    }

    setUploading(true);
    setMessage(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/image-upload/feature-image`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      
      if (data.success) {
        // Update the hero_image field with the new URL
        const fullUrl = `${API_URL}${data.url}`;
        handleFieldChange('hero_image', fullUrl);
        setMessage({ type: 'success', text: `Image uploaded: ${file.name}` });
      } else {
        setMessage({ type: 'error', text: data.detail || 'Upload failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error uploading image' });
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden" data-testid="feature-editor">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-4">
        <h2 className="text-xl font-bold">🎨 Feature Content Editor</h2>
        <p className="text-sm text-orange-100">Edit hero images and text content for all 31 AI features</p>
      </div>

      <div className="flex gap-2 border-b bg-slate-50 px-4 py-3" data-testid="feature-editor-mode-tabs">
        <button
          onClick={() => setEditorMode('feature')}
          className={`rounded-full px-4 py-2 text-sm font-semibold ${editorMode === 'feature' ? 'bg-orange-500 text-white' : 'bg-white text-slate-600 border border-slate-200'}`}
          data-testid="feature-editor-tab-features"
        >
          AI Feature Content
        </button>
        <button
          onClick={() => setEditorMode('brand')}
          className={`rounded-full px-4 py-2 text-sm font-semibold ${editorMode === 'brand' ? 'bg-orange-500 text-white' : 'bg-white text-slate-600 border border-slate-200'}`}
          data-testid="feature-editor-tab-brand"
        >
          Brand Pages
        </button>
      </div>

      {editorMode === 'brand' ? (
        <div className="p-4">
          <BrandContentEditor />
        </div>
      ) : (

      <div className="flex">
        {/* Feature List Sidebar */}
        <div className="w-1/3 border-r max-h-[600px] overflow-y-auto">
          <div className="p-3 bg-gray-50 border-b sticky top-0">
            <input
              type="text"
              placeholder="Search features..."
              className="w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>
          {features.map((feature, idx) => (
            <div
              key={idx}
              onClick={() => selectFeature(feature)}
              className={`p-3 border-b cursor-pointer hover:bg-orange-50 transition ${
                selectedFeature?.day === feature.day ? 'bg-orange-100 border-l-4 border-l-orange-500' : ''
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs text-gray-500">#{feature.day}</span>
                  <h4 className="font-medium text-sm">{feature.feature}</h4>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    feature.category === 'admin' ? 'bg-orange-100 text-orange-700' :
                    feature.category === 'teacher' ? 'bg-blue-100 text-blue-700' :
                    feature.category === 'student' ? 'bg-green-100 text-green-700' :
                    'bg-purple-100 text-purple-700'
                  }`}>
                    {feature.category}
                  </span>
                </div>
                {customizations[feature.day] && (
                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">
                    Customized
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Editor Panel */}
        <div className="w-2/3 p-4 max-h-[600px] overflow-y-auto">
          {!selectedFeature ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
              <span className="text-4xl mb-2">👈</span>
              <p>Select a feature to edit</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Feature Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold">#{selectedFeature.day} - {selectedFeature.feature}</h3>
                  <p className="text-sm text-gray-500">{selectedFeature.title_hi}</p>
                </div>
                <div className="flex gap-2">
                  {!editMode ? (
                    <button
                      onClick={() => setEditMode(true)}
                      className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition text-sm"
                    >
                      ✏️ Edit
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => setEditMode(false)}
                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveChanges}
                        disabled={saving}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition text-sm disabled:opacity-50"
                      >
                        {saving ? 'Saving...' : '💾 Save'}
                      </button>
                    </>
                  )}
                  {customizations[selectedFeature.day] && (
                    <button
                      onClick={revertToDefault}
                      className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition text-sm"
                    >
                      🔄 Revert
                    </button>
                  )}
                </div>
              </div>

              {/* Message */}
              {message && (
                <div className={`p-3 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {message.text}
                </div>
              )}

              {/* Hero Image */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Hero Image</label>
                  {editMode && (
                    <div className="flex gap-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleImageUpload}
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        className="hidden"
                        id="hero-image-upload"
                      />
                      <label
                        htmlFor="hero-image-upload"
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg cursor-pointer transition ${
                          uploading 
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                            : 'bg-blue-500 text-white hover:bg-blue-600'
                        }`}
                      >
                        {uploading ? (
                          <span className="flex items-center gap-1">
                            <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                            </svg>
                            Uploading...
                          </span>
                        ) : (
                          '📤 Upload Image'
                        )}
                      </label>
                    </div>
                  )}
                </div>
                
                {editMode ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={editedFields.hero_image || ''}
                      onChange={(e) => handleFieldChange('hero_image', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                      placeholder="https://... (or upload an image)"
                    />
                    <p className="text-xs text-gray-500">Paste URL or upload image (max 5MB, JPG/PNG/GIF/WebP)</p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600 break-all">{featureDetail?.hero_image || 'Default image'}</p>
                )}
                {featureDetail?.hero_image && (
                  <img 
                    src={editMode && editedFields.hero_image ? editedFields.hero_image : featureDetail.hero_image} 
                    alt="Hero" 
                    className="mt-2 w-full h-32 object-cover rounded-lg"
                  />
                )}
              </div>

              {/* Problems Section */}
              <div className="border rounded-lg p-4 bg-red-50/50">
                <h4 className="font-medium text-red-700 mb-3">😟 Problems Section</h4>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Title (Hindi)</label>
                    {editMode ? (
                      <input
                        type="text"
                        value={editedFields.problems_title_hi || ''}
                        onChange={(e) => handleFieldChange('problems_title_hi', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      />
                    ) : (
                      <p className="text-sm bg-white p-2 rounded">{featureDetail?.problems?.title_hi}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Description (Hindi)</label>
                    {editMode ? (
                      <textarea
                        value={editedFields.problems_description_hi || ''}
                        onChange={(e) => handleFieldChange('problems_description_hi', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        rows={3}
                      />
                    ) : (
                      <p className="text-sm bg-white p-2 rounded">{featureDetail?.problems?.description_hi}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Solution Section */}
              <div className="border rounded-lg p-4 bg-green-50/50">
                <h4 className="font-medium text-green-700 mb-3">✅ Solution Section</h4>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Title (Hindi)</label>
                    {editMode ? (
                      <input
                        type="text"
                        value={editedFields.solution_title_hi || ''}
                        onChange={(e) => handleFieldChange('solution_title_hi', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      />
                    ) : (
                      <p className="text-sm bg-white p-2 rounded">{featureDetail?.solution?.title_hi}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Description (Hindi)</label>
                    {editMode ? (
                      <textarea
                        value={editedFields.solution_description_hi || ''}
                        onChange={(e) => handleFieldChange('solution_description_hi', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        rows={3}
                      />
                    ) : (
                      <p className="text-sm bg-white p-2 rounded">{featureDetail?.solution?.description_hi}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Benefits Preview */}
              {featureDetail?.benefits && Array.isArray(featureDetail.benefits) && featureDetail.benefits.length > 0 && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium text-gray-700 mb-3">🎯 Benefits (Read-only)</h4>
                  <ul className="space-y-2">
                    {featureDetail.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-green-500">✓</span>
                        <span>{benefit.text_hi || benefit.text_en || benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
